import React, { useState, useEffect } from 'react';

const WebhookChannelForm = ({ channel, orgId, teamId, onSave, onCancel, apiCall }) => {
  const isTeamMode = !!teamId && !orgId;
  const [formData, setFormData] = useState({
    channelName: '',
    webhookUrl: '',
    httpMethod: 'POST',
    isActive: true,
    isPrimary: false,
    authType: 'None',
    authToken: '',
    authValue: '',
    customHeaders: [{ key: '', value: '' }],
    requestBodyTemplate: `{
  "alertId": "{{alertId}}",
  "ruleName": "{{ruleName}}",
  "severity": "{{severity}}",
  "message": "{{message}}",
  "triggeredAt": "{{triggeredAt}}",
  "organizationId": {{organizationId}},
  "teamId": {{teamId}},
  "details": {{details}}
}`,
    timeoutSeconds: 30,
    retryAttempts: 3,
    verifySsl: true,
    expectedStatusCodes: '200-299',
    organizationId: orgId,
    teamId: teamId
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (channel) {
      loadChannelData();
    }
  }, [channel]);

  const loadChannelData = async () => {
    try {
      const endpoint = isTeamMode
        ? `/api/alertchannelsapi/team/${teamId}/${channel.id}`
        : `/api/alertchannelsapi/${channel.id}`;
      const data = await apiCall(endpoint);
      const headers = data.customHeaders || {};
      const headerArray = Object.keys(headers).map(key => ({ key, value: headers[key] }));
      if (headerArray.length === 0) {
        headerArray.push({ key: '', value: '' });
      }

      setFormData({
        channelName: data.channelName || '',
        webhookUrl: data.webhookUrl || '',
        httpMethod: data.httpMethod || 'POST',
        isActive: data.isActive !== false,
        isPrimary: data.isPrimary === true,
        authType: data.authType || 'None',
        authToken: data.authToken || '',
        authValue: data.authValue || '',
        customHeaders: headerArray,
        requestBodyTemplate: data.requestBodyTemplate || formData.requestBodyTemplate,
        timeoutSeconds: data.timeoutSeconds || 30,
        retryAttempts: data.retryAttempts || 3,
        verifySsl: data.verifySsl !== false,
        expectedStatusCodes: data.expectedStatusCodes || '200-299',
        organizationId: orgId,
        teamId: teamId
      });
    } catch (err) {
      setError(err.message);
    }
  };

  const handleAddHeader = () => {
    setFormData(prev => ({
      ...prev,
      customHeaders: [...prev.customHeaders, { key: '', value: '' }]
    }));
  };

  const handleRemoveHeader = (index) => {
    setFormData(prev => ({
      ...prev,
      customHeaders: prev.customHeaders.filter((_, i) => i !== index)
    }));
  };

  const handleHeaderChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      customHeaders: prev.customHeaders.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!formData.webhookUrl.trim()) {
      setError('Webhook URL is required');
      return;
    }

    try {
      new URL(formData.webhookUrl);
    } catch {
      setError('Invalid webhook URL format');
      return;
    }

    if (formData.authType !== 'None' && !formData.authToken.trim()) {
      setError('Auth token is required when authentication is enabled');
      return;
    }

    setLoading(true);
    try {
      const headersObj = {};
      formData.customHeaders.forEach(h => {
        if (h.key.trim() && h.value.trim()) {
          headersObj[h.key.trim()] = h.value.trim();
        }
      });

      const payload = {
        ...formData,
        customHeaders: headersObj,
        channelId: channel?.id
      };

      if (channel) {
        // Update existing channel
        const updateEndpoint = isTeamMode
          ? `/api/alertchannelsapi/team/${teamId}/${channel.id}`
          : `/api/alertchannelsapi/${channel.id}`;
        await apiCall(updateEndpoint, {
          method: 'PUT',
          body: JSON.stringify(payload)
        });
      } else {
        // Create new channel
        const createEndpoint = isTeamMode
          ? '/api/alertchannelsapi/team/webhook'
          : '/api/alertchannelsapi/webhook';
        await apiCall(createEndpoint, {
          method: 'POST',
          body: JSON.stringify(payload)
        });
      }
      onSave();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h3 style={{ margin: 0 }}>{channel ? 'Edit' : 'Create'} Webhook Channel</h3>
        <button
          type="button"
          onClick={onCancel}
          style={{
            border: 'none',
            background: 'none',
            fontSize: '24px',
            cursor: 'pointer',
            color: '#6b7280'
          }}
        >
          ×
        </button>
      </div>

      {error && (
        <div style={{
          padding: '12px',
          marginBottom: '16px',
          backgroundColor: '#fee2e2',
          color: '#991b1b',
          borderRadius: '6px'
        }}>
          {error}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <div>
          <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
            Channel Name *
          </label>
          <input
            type="text"
            value={formData.channelName}
            onChange={(e) => setFormData(prev => ({ ...prev, channelName: e.target.value }))}
            required
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px'
            }}
          />
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
            Webhook URL *
          </label>
          <input
            type="url"
            value={formData.webhookUrl}
            onChange={(e) => setFormData(prev => ({ ...prev, webhookUrl: e.target.value }))}
            required
            placeholder="https://example.com/webhook"
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px'
            }}
          />
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
            HTTP Method
          </label>
          <select
            value={formData.httpMethod}
            onChange={(e) => setFormData(prev => ({ ...prev, httpMethod: e.target.value }))}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px'
            }}
          >
            <option value="POST">POST</option>
            <option value="PUT">PUT</option>
            <option value="PATCH">PATCH</option>
          </select>
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
            Authentication Type
          </label>
          <select
            value={formData.authType}
            onChange={(e) => setFormData(prev => ({ ...prev, authType: e.target.value }))}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px'
            }}
          >
            <option value="None">None</option>
            <option value="Bearer">Bearer Token</option>
            <option value="Basic">Basic Auth</option>
            <option value="ApiKey">API Key</option>
          </select>
        </div>

        {formData.authType !== 'None' && (
          <>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
                {formData.authType === 'ApiKey' ? 'API Key Name' : 'Token/Key'} *
              </label>
              <input
                type="text"
                value={formData.authToken}
                onChange={(e) => setFormData(prev => ({ ...prev, authToken: e.target.value }))}
                required={formData.authType !== 'None'}
                placeholder={formData.authType === 'ApiKey' ? 'X-API-Key' : 'token'}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            </div>

            {(formData.authType === 'Basic' || formData.authType === 'ApiKey') && (
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
                  {formData.authType === 'Basic' ? 'Password' : 'API Key Value'} *
                </label>
                <input
                  type="password"
                  value={formData.authValue}
                  onChange={(e) => setFormData(prev => ({ ...prev, authValue: e.target.value }))}
                  required
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>
            )}
          </>
        )}

        <div>
          <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
            Custom Headers
          </label>
          {formData.customHeaders.map((header, index) => (
            <div key={index} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
              <input
                type="text"
                value={header.key}
                onChange={(e) => handleHeaderChange(index, 'key', e.target.value)}
                placeholder="Header Name"
                style={{
                  flex: 1,
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
              <input
                type="text"
                value={header.value}
                onChange={(e) => handleHeaderChange(index, 'value', e.target.value)}
                placeholder="Header Value"
                style={{
                  flex: 1,
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
              <button
                type="button"
                onClick={() => handleRemoveHeader(index)}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #dc2626',
                  borderRadius: '6px',
                  backgroundColor: '#ffffff',
                  color: '#dc2626',
                  cursor: 'pointer'
                }}
              >
                Remove
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={handleAddHeader}
            style={{
              padding: '6px 12px',
              fontSize: '12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              backgroundColor: '#ffffff',
              cursor: 'pointer'
            }}
          >
            + Add Header
          </button>
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
            Request Body Template
          </label>
          <textarea
            value={formData.requestBodyTemplate}
            onChange={(e) => setFormData(prev => ({ ...prev, requestBodyTemplate: e.target.value }))}
            rows={10}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
              fontFamily: 'monospace'
            }}
          />
          <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
            Available variables: {'{{alertId}}'}, {'{{ruleName}}'}, {'{{severity}}'}, {'{{message}}'}, {'{{triggeredAt}}'}, {'{{organizationId}}'}, {'{{teamId}}'}, {'{{details}}'}
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
              Timeout (seconds)
            </label>
            <input
              type="number"
              min="5"
              max="300"
              value={formData.timeoutSeconds}
              onChange={(e) => setFormData(prev => ({ ...prev, timeoutSeconds: parseInt(e.target.value) || 30 }))}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
              Retry Attempts
            </label>
            <input
              type="number"
              min="0"
              max="5"
              value={formData.retryAttempts}
              onChange={(e) => setFormData(prev => ({ ...prev, retryAttempts: parseInt(e.target.value) || 3 }))}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            />
          </div>
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
            Expected Status Codes
          </label>
          <input
            type="text"
            value={formData.expectedStatusCodes}
            onChange={(e) => setFormData(prev => ({ ...prev, expectedStatusCodes: e.target.value }))}
            placeholder="200-299"
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px'
            }}
          />
        </div>

        <div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={formData.verifySsl}
              onChange={(e) => setFormData(prev => ({ ...prev, verifySsl: e.target.checked }))}
            />
            Verify SSL Certificate
          </label>
        </div>

        <div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={formData.isActive}
              onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
            />
            Active
          </label>
        </div>

        <div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={formData.isPrimary}
              onChange={(e) => setFormData(prev => ({ ...prev, isPrimary: e.target.checked }))}
            />
            Set as Primary Channel
          </label>
        </div>

        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '16px' }}>
          <button
            type="button"
            onClick={onCancel}
            style={{
              padding: '10px 20px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              backgroundColor: '#ffffff',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            style={{
              padding: '10px 20px',
              border: 'none',
              borderRadius: '6px',
              backgroundColor: '#3b82f6',
              color: '#ffffff',
              cursor: loading ? 'wait' : 'pointer',
              fontSize: '14px',
              fontWeight: 500,
              opacity: loading ? 0.6 : 1
            }}
          >
            {loading ? 'Saving...' : (channel ? 'Update' : 'Create')}
          </button>
        </div>
      </div>
    </form>
  );
};

export default WebhookChannelForm;

